import type { Membership } from "@how/app-kit";

/** Where the staff address sends a signed-in login with no church in the path (ADR 0068). */
export type DoorLanding =
  | { readonly kind: "loading" }
  | { readonly kind: "church"; readonly slug: string }
  | {
      readonly kind: "staff-unavailable";
      readonly church: UnavailableStaffChurch;
      readonly churches: readonly Membership[];
    }
  | { readonly kind: "member-only"; readonly churches: readonly Membership[] }
  | { readonly kind: "no-access" };

/**
 * The login's one staff church (ADR 0068): a staff-side role, invited or active, at an active church. The database
 * allows at most one; should old data hold two, the first by name (my_memberships' order) is taken.
 */
export function staffChurchOf(memberships: readonly Membership[]): Membership | undefined {
  return memberships.find(
    (m) =>
      m.hasStaffAccess &&
      m.tenantStatus === "active" &&
      (m.membershipStatus === "active" || m.membershipStatus === "invited"),
  );
}

/**
 * Why a login's staff church cannot be opened (ADR 0068 R16): the staff access there is suspended, the church is
 * suspended, or the church is otherwise not available (provisioning, offboarding). The database's words, from
 * my_unavailable_staff_churches.
 */
export type StaffUnavailableReason =
  "membership_suspended" | "church_suspended" | "church_unavailable";

/** A staff church the login holds but cannot open, and why (my_unavailable_staff_churches; ruling P16). */
export interface UnavailableStaffChurch {
  readonly tenantId: string;
  readonly slug: string;
  readonly name: string;
  readonly reason: StaffUnavailableReason;
}

/** What the staff address says about a staff church it cannot open (P16, P17). */
export function staffUnavailableMessage(church: UnavailableStaffChurch): string {
  switch (church.reason) {
    case "membership_suspended":
      return `Your staff access at ${church.name} is suspended. Ask an administrator there to restore it.`;
    case "church_suspended":
      return `${church.name}'s HoW account is suspended. Contact your church's administrator or DCX Logic support.`;
    case "church_unavailable":
      return `${church.name}'s HoW account isn't available right now. Contact your church's administrator or DCX Logic support.`;
  }
}

/** The member churches whose portals the staff address offers: active, at an active church, not staff-side. */
export function memberChurchesOf(memberships: readonly Membership[]): Membership[] {
  return memberships.filter(
    (m) => !m.hasStaffAccess && m.membershipStatus === "active" && m.tenantStatus === "active",
  );
}

/**
 * The staff church; else a staff church that cannot be opened, with the reason (P16); else the member churches'
 * portals, where My schedule now lives (ADR 0070 R12; this ends ADR 0068 R1 and ADR 0066 R8); else no access.
 * `unavailable` (my_unavailable_staff_churches) is undefined while still unknown.
 */
export function doorLanding(
  memberships: readonly Membership[] | undefined,
  unavailable: readonly UnavailableStaffChurch[] | undefined,
): DoorLanding {
  if (memberships === undefined) return { kind: "loading" };
  const staff = staffChurchOf(memberships);
  if (staff !== undefined) return { kind: "church", slug: staff.slug };
  if (unavailable === undefined) return { kind: "loading" };
  const first = unavailable[0];
  if (first !== undefined) {
    return { kind: "staff-unavailable", church: first, churches: memberChurchesOf(memberships) };
  }
  const churches = memberChurchesOf(memberships);
  return churches.length > 0 ? { kind: "member-only", churches } : { kind: "no-access" };
}
